import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, Leaf, FileText, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { NotificationBell } from '../NotificationBell';
import { useLanguage, LANGUAGES } from '../../context/LanguageContext';

export const Header = () => {
    const { user, logout, isAdmin, isSupplier, isAuthenticated } = useAuth();
    const { orderCount, quoteCount, openOrderCart, openQuoteCart } = useCart();
    const { t, lang: currentLang, changeLang } = useLanguage();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const navLinks = [
        { href: '/catalogue', label: t.catalogue },
        { href: '/contact', label: t.contact },
    ];

    if (isAuthenticated && !isAdmin && !isSupplier) {
        navLinks.push({ href: '/dashboard', label: t.monEspace });
    }
    if (isAuthenticated && isSupplier) {
        navLinks.push({ href: '/fournisseur', label: t.monEspace });
    }

    return (
        <header className="sticky top-0 z-50 w-full border-b border-leaf/10 bg-sand/80 backdrop-blur-xl" data-testid="header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
                        <div className="w-10 h-10 rounded-xl bg-jungle flex items-center justify-center group-hover:scale-105 transition-transform">
                            <Leaf className="w-6 h-6 text-sand" />
                        </div>
                        <span className="font-heading font-bold text-xl text-jungle">PRIXDEAL</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-6">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                className="text-sm font-medium text-jungle/70 hover:text-leaf transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-leaf hover:after:w-full after:transition-all"
                                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                        {isAuthenticated && !isAdmin && (
                            <>
                                {/* Order Cart Button */}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="relative hover:bg-jungle/10"
                                    onClick={openOrderCart}
                                    data-testid="cart-order-button"
                                    title="Panier commande"
                                >
                                    <ShoppingCart className="h-5 w-5 text-jungle" />
                                    {orderCount > 0 && (
                                        <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-emerald-600 text-white border-2 border-sand text-[10px]">
                                            {orderCount}
                                        </Badge>
                                    )}
                                </Button>

                                {/* Quote Cart Button */}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="relative hover:bg-amber-100/50"
                                    onClick={openQuoteCart}
                                    data-testid="cart-quote-button"
                                    title="Panier demande de prix"
                                >
                                    <FileText className="h-5 w-5 text-amber-700" />
                                    {quoteCount > 0 && (
                                        <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-amber-500 text-white border-2 border-sand text-[10px]">
                                            {quoteCount}
                                        </Badge>
                                    )}
                                </Button>
                            </>
                        )}

                        {/* Language Selector */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="hover:bg-jungle/10" data-testid="language-selector-btn">
                                    <span className="text-base">{LANGUAGES.find(l => l.code === currentLang)?.flag || '🌐'}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44 bg-white/95 backdrop-blur-xl">
                                {LANGUAGES.map(lang => (
                                    <DropdownMenuItem
                                        key={lang.code}
                                        onClick={() => changeLang(lang.code)}
                                        className={`cursor-pointer ${currentLang === lang.code ? 'bg-leaf/10 font-semibold' : ''}`}
                                        data-testid={`lang-${lang.code}`}
                                    >
                                        <span className="mr-2 text-base">{lang.flag}</span>
                                        {lang.label}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Notification Bell for clients */}
                        <NotificationBell />

                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="hover:bg-leaf/10" data-testid="user-menu-button">
                                        <User className="h-5 w-5 text-jungle" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 bg-white/95 backdrop-blur-xl">
                                    <div className="px-2 py-1.5">
                                        <p className="text-sm font-medium text-jungle">{user?.contact_name}</p>
                                        <p className="text-xs text-jungle/60">{user?.company_name}</p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem asChild>
                                        <Link to={isAdmin ? '/admin' : isSupplier ? '/fournisseur' : '/dashboard'} className="cursor-pointer" data-testid="dashboard-link">
                                            <LayoutDashboard className="mr-2 h-4 w-4" />
                                            {isAdmin ? 'Administration' : isSupplier ? 'Espace Fournisseur' : 'Tableau de bord'}
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-button">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Deconnexion
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Button asChild className="bg-jungle hover:bg-jungle/90 text-sand" data-testid="login-button">
                                <Link to="/connexion">{t.connexion}</Link>
                            </Button>
                        )}

                        {/* Mobile Menu */}
                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                            <SheetTrigger asChild className="md:hidden">
                                <Button variant="ghost" size="icon" className="hover:bg-leaf/10" data-testid="mobile-menu-button">
                                    <Menu className="h-5 w-5 text-jungle" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-72 bg-sand">
                                <nav className="flex flex-col gap-4 mt-8">
                                    {navLinks.map((link) => (
                                        <Link
                                            key={link.href}
                                            to={link.href}
                                            className="text-lg font-medium text-jungle hover:text-leaf transition-colors"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </header>
    );
};
